'use client';

import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser, useOrganization, useOrganizationList } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';

import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { PUBLIC_ROUTES } from '@/config/router';
import { Copy, Loader2 } from 'lucide-react';
import { trpc } from '@/lib/api/trpc';
import { useGetAccountInfo, useUpdateAccount } from '@/lib/api/account';
import { cn } from '@/lib/utils';
import { useReferralEnabled } from '@/hooks/use-referral-enabled';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  bio: string;
  website: string;
  location: string;
  timezone: string;
  avatar: string;
}

export default function ProfilePage() {
  const { t } = useTranslation(['profile', 'common']);
  const { user, isLoaded, isSignedIn } = useUser();
  const { organization, membership } = useOrganization();
  const { userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const { data: account } = useGetAccountInfo();
  const updateAccountMutation = useUpdateAccount();

  // Form state
  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    bio: '',
    website: '',
    location: '',
    timezone: 'UTC',
    avatar: '',
  });

  // Referral program is tagada-specific: hidden under whitelabel brands
  // (apptics/fmj), partner-mode hosts, and reseller/whitelabel orgs. Shared
  // with the sidebar + referral page via the same hook so they never diverge.
  const DISPLAY_REFERRAL = useReferralEnabled();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  const [isSubmitting, setIsSubmitting] = useState(false);




  // Add a file input reference
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Initialize form data after user data is loaded. Timezone is the
  // account-level (per-org) preference, not a Clerk user metadata value,
  // so it comes from the tRPC account query.
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.primaryEmailAddress?.emailAddress || '',
        username: user.username || '',
        bio: (user.publicMetadata?.bio as string) || '',
        website: (user.publicMetadata?.website as string) || '',
        location: (user.publicMetadata?.location as string) || '',
        timezone: account?.timezone || 'UTC',
        avatar: user.imageUrl || '',
      });
    }
  }, [user, account?.timezone]);

  // Handle loading state and redirect if not signed in
  if (!isLoaded) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">{t('profile:loadingProfile')}</div>
      </div>
    );
  }

  if (!isSignedIn) {
    navigate(PUBLIC_ROUTES.SIGN_IN);
    return null;
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Add a function to handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0 || !user) {
      return;
    }

    const file = e.target.files[0];

    // Check file size (max 1MB)
    if (file.size > 1024 * 1024) {
      toast({
        title: t('profile:fileTooLarge'),
        description: t('profile:fileTooLargeDescription'),
        variant: 'destructive',
      });
      return;
    }

    // Check file type
    if (!/image\/(jpeg|jpg|png|gif)/i.exec(file.type)) {
      toast({
        title: t('profile:invalidFileType'),
        description: t('profile:invalidFileTypeDescription'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Upload image to Clerk
      const formData = new FormData();
      formData.append('file', file);

      // Update the user's profile image
      await user.setProfileImage({ file });

      // Update the local form data with the new image URL
      // Note: Clerk takes care of processing and hosting the image
      // We'll need to fetch the updated user to get the new image URL
      const updatedUser = await user.reload();

      setFormData((prev) => ({
        ...prev,
        avatar: updatedUser.imageUrl || prev.avatar,
      }));

      toast({
        title: t('profile:profilePictureUpdated'),
        description: t('profile:profilePictureUpdatedDescription'),
      });
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      toast({
        title: t('profile:uploadFailed'),
        description: t('profile:uploadFailedDescription'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!user) throw new Error('User not found');

      // Prepare the data for Clerk API
      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.username,
      };

      // Prepare metadata for additional profile fields
      const userMetadata = {
        // Keep existing metadata and update with new values. unsafeMetadata
        // spreads last so keys like `acquisition` (first-touch attribution,
        // read by the CRM admin) survive profile saves.
        ...(user.publicMetadata as Record<string, unknown>),
        ...(user.unsafeMetadata as Record<string, unknown>),
        bio: formData.bio,
        website: formData.website,
        location: formData.location,
      };

      // Update the Clerk user profile + the tenant's account timezone in
      // parallel. Account timezone drives entity date rendering across the
      // CRM (orders/payments/subs/customers).
      await Promise.all([
        user.update({
          ...userData,
          unsafeMetadata: userMetadata,
        }),
        formData.timezone !== account?.timezone
          ? updateAccountMutation.mutateAsync({ timezone: formData.timezone })
          : Promise.resolve(),
      ]);

      toast({
        title: t('profile:profileUpdated'),
        description: t('profile:profileUpdatedDescription'),
      });

      // Return to overview tab after successful update
      setActiveTab('overview');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: t('profile:error'),
        description: t('profile:errorUpdatingProfile'),
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="container mx-auto px-4 py-6 md:px-6">
      <Toaster />
      <div className="flex flex-col gap-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t('profile:title')}</h1>
          <Button onClick={() => navigate('/')}>{t('profile:backToOverview')}</Button>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {/* Profile Summary Card */}
          <Card className="md:col-span-1">
            <CardHeader className="pb-4">
              <CardTitle>{t('profile:profileSummary')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col items-center space-y-4 text-center">
              <Avatar className="h-24 w-24">
                <AvatarImage src={user?.imageUrl} alt={user?.fullName || 'User'} />
                <AvatarFallback>
                  {user?.firstName?.charAt(0)}
                  {user?.lastName?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h3 className="text-xl font-semibold">{user?.fullName}</h3>
                <p className="text-muted-foreground text-sm">{user?.primaryEmailAddress?.emailAddress}</p>
              </div>
              <Badge variant="outline" className="px-3 py-1">
                {user?.publicMetadata?.role ? String(user.publicMetadata.role) : t('profile:memberFallback')}
              </Badge>
              <div className="flex w-full flex-col space-y-2">
                <Button variant="outline" size="sm" onClick={() => setActiveTab('edit')}>
                  {t('profile:editProfile')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Profile Content */}
          <div className="md:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className={cn("w-full sm:grid", DISPLAY_REFERRAL ? 'sm:grid-cols-5' : 'sm:grid-cols-4')}>
                <TabsTrigger value="overview">{t('profile:overview')}</TabsTrigger>
                <TabsTrigger value="organization">{t('profile:organizationTab')}</TabsTrigger>
                <TabsTrigger value="memberships">{t('profile:membershipsTab')}</TabsTrigger>
                <TabsTrigger value="edit">{t('profile:editProfile')}</TabsTrigger>
                {DISPLAY_REFERRAL && <TabsTrigger value="referral" onClick={(e) => { e.preventDefault(); navigate(`/org/${orgSlug}/referral`); }}>{t('profile:referral')}</TabsTrigger>}
              </TabsList>

              <TabsContent value="overview" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('profile:profileOverview')}</CardTitle>
                    <CardDescription>{t('profile:viewYourInfo')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground text-sm font-medium">{t('profile:userId')}</Label>
                        <CopyableResourceBadge
                          name="User ID"
                          value={user?.id || ''}
                          className="font-mono text-xs"
                        />
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">{t('profile:fullName')}</h4>
                        <p>{user?.fullName}</p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">{t('profile:email')}</h4>
                        <p>{user?.primaryEmailAddress?.emailAddress}</p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">{t('profile:username')}</h4>
                        <p>{user?.username || t('profile:notSet')}</p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">{t('profile:location')}</h4>
                        <p>{formData.location || t('profile:notSet')}</p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">{t('profile:website')}</h4>
                        <p>{formData.website || t('profile:notSet')}</p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">{t('profile:timezone')}</h4>
                        <p>{formData.timezone}</p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">
                          {t('profile:accountCreated')}
                        </h4>
                        <p>{new Date(user?.createdAt || '').toLocaleDateString()}</p>
                      </div>
                      <div>
                        <h4 className="text-muted-foreground text-sm font-medium">
                          {t('profile:lastSignIn')}
                        </h4>
                        <p>{new Date(user?.lastSignInAt || '').toLocaleDateString()}</p>
                      </div>
                    </div>

                    {formData.bio && (
                      <>
                        <Separator />
                        <div>
                          <h4 className="text-muted-foreground mb-2 text-sm font-medium">
                            {t('profile:bio')}
                          </h4>
                          <p className="text-sm">{formData.bio}</p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="organization" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('profile:organizationMembership')}</CardTitle>
                    <CardDescription>{t('profile:yourRoleAndPermissions')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {organization ? (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-sm font-medium">
                            {t('profile:organizationName')}
                          </Label>
                          <p className="text-sm font-semibold">{organization.name}</p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-sm font-medium">{t('profile:organizationId')}</Label>
                          <CopyableResourceBadge
                            name="Organization ID"
                            value={organization.id}
                            className="font-mono text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-sm font-medium">{t('profile:slug')}</Label>
                          <CopyableResourceBadge
                            name="Slug"
                            value={organization.slug || 'N/A'}
                            className="font-mono text-xs"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-sm font-medium">{t('profile:yourRole')}</Label>
                          <Badge variant={membership?.role === 'org:admin' ? 'destructive' : 'secondary'}>
                            {membership?.role?.replace('org:', '') || t('profile:memberFallback')}
                          </Badge>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-sm font-medium">{t('profile:memberSince')}</Label>
                          <p className="text-sm">
                            {membership?.createdAt
                              ? new Date(membership.createdAt).toLocaleDateString()
                              : 'N/A'}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-sm font-medium">{t('profile:totalMembers')}</Label>
                          <p className="text-sm">
                            {organization.membersCount || 0}
                            {organization.maxAllowedMemberships
                              ? ` / ${organization.maxAllowedMemberships}`
                              : ''}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">{t('profile:noOrganizationSelected')}</p>
                    )}

                    {membership?.permissions && membership.permissions.length > 0 && (
                      <>
                        <Separator />
                        <div className="space-y-2">
                          <Label className="text-muted-foreground text-sm font-medium">
                            {t('profile:yourPermissions')}
                          </Label>
                          <div className="flex flex-wrap gap-2">
                            {membership.permissions.map((permission, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {permission}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="memberships" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>{t('profile:allOrganizationMemberships')}</CardTitle>
                    <CardDescription>{t('profile:yourMembershipsAcrossAllOrgs')}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {userMemberships?.data && userMemberships.data.length > 0 ? (
                      <div className="space-y-4">
                        {userMemberships.data.map((membership) => {
                          const isActive = membership.organization.id === organization?.id;

                          return (
                            <div
                              key={membership.id}
                              className={`rounded-lg border p-4 ${isActive
                                ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'
                                : 'border-border'
                                }`}
                            >
                              <div className="mb-3 flex items-start justify-between">
                                <div className="space-y-1">
                                  <h4 className="text-sm font-medium">{membership.organization.name}</h4>
                                  <CopyableResourceBadge
                                    name="Organization ID"
                                    value={membership.organization.id}
                                    className="font-mono text-xs"
                                  />
                                </div>
                                {isActive && (
                                  <Badge variant="green" className="text-xs">
                                    {t('profile:current')}
                                  </Badge>
                                )}
                              </div>

                              <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                                <div className="space-y-1">
                                  <Label className="text-muted-foreground text-xs">{t('profile:role')}</Label>
                                  <Badge
                                    variant={membership.role === 'org:admin' ? 'destructive' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {membership.role.replace('org:', '')}
                                  </Badge>
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-muted-foreground text-xs">{t('profile:membershipId')}</Label>
                                  <CopyableResourceBadge
                                    name="Membership ID"
                                    value={membership.id}
                                    className="font-mono text-xs"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-muted-foreground text-xs">{t('profile:joined')}</Label>
                                  <p className="text-sm">
                                    {new Date(membership.createdAt).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>

                              {membership.permissions && membership.permissions.length > 0 && (
                                <div className="mt-3 border-t pt-3">
                                  <Label className="text-muted-foreground mb-2 block text-xs">
                                    {t('profile:permissions')}
                                  </Label>
                                  <div className="flex flex-wrap gap-1">
                                    {membership.permissions.map((permission, index) => (
                                      <Badge key={index} variant="outline" className="text-xs">
                                        {permission}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">{t('profile:noOrganizationMemberships')}</p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="edit" className="mt-4">
                <Card>
                  <form onSubmit={handleSubmit}>
                    <CardHeader>
                      <CardTitle>{t('profile:editProfile')}</CardTitle>
                      <CardDescription>{t('profile:updateYourInfo')}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">{t('profile:personalInfo')}</h3>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">{t('profile:firstName')}</Label>
                            <Input
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="lastName">{t('profile:lastName')}</Label>
                            <Input
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email">{t('profile:emailAddress')}</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled
                          />
                          <p className="text-muted-foreground text-xs">
                            {t('profile:contactSupportToChangeEmail')}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="username">{t('profile:username')}</Label>
                          <Input
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleInputChange}
                          />
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">{t('profile:aboutYou')}</h3>

                        <div className="space-y-2">
                          <Label htmlFor="bio">{t('profile:bio')}</Label>
                          <Input
                            id="bio"
                            name="bio"
                            value={formData.bio}
                            onChange={handleInputChange}
                            placeholder={t('profile:tellUsAboutYourself')}
                          />
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="website">{t('profile:website')}</Label>
                            <Input
                              id="website"
                              name="website"
                              type="url"
                              value={formData.website}
                              onChange={handleInputChange}
                              placeholder="https://example.com"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="location">{t('profile:location')}</Label>
                            <Input
                              id="location"
                              name="location"
                              value={formData.location}
                              onChange={handleInputChange}
                              placeholder={t('profile:locationPlaceholder')}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="timezone">{t('profile:timezone')}</Label>
                          <Select
                            value={formData.timezone}
                            onValueChange={(value) => handleSelectChange('timezone', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={t('profile:selectTimezone')} />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="UTC">UTC</SelectItem>
                              <SelectItem value="America/New_York">Eastern Time (ET)</SelectItem>
                              <SelectItem value="America/Chicago">Central Time (CT)</SelectItem>
                              <SelectItem value="America/Denver">Mountain Time (MT)</SelectItem>
                              <SelectItem value="America/Los_Angeles">Pacific Time (PT)</SelectItem>
                              <SelectItem value="Europe/London">London (GMT)</SelectItem>
                              <SelectItem value="Europe/Paris">Paris (CET)</SelectItem>
                              <SelectItem value="Asia/Tokyo">Tokyo (JST)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <Separator />

                      <div className="space-y-4">
                        <h3 className="text-lg font-medium">{t('profile:profilePicture')}</h3>
                        <div className="flex items-center gap-4">
                          <Avatar className="h-16 w-16">
                            <AvatarImage
                              src={formData.avatar}
                              alt={`${formData.firstName} ${formData.lastName}`}
                            />
                            <AvatarFallback>
                              {formData.firstName.charAt(0)}
                              {formData.lastName.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="space-y-1">
                            <input
                              type="file"
                              id="profile-picture"
                              className="hidden"
                              accept="image/jpeg,image/jpg,image/png,image/gif"
                              onChange={handleFileSelect}
                              ref={fileInputRef}
                            />
                            <Button
                              variant="outline"
                              type="button"
                              size="sm"
                              onClick={() => fileInputRef.current?.click()}
                              disabled={isSubmitting}
                            >
                              {t('profile:uploadNewPicture')}
                            </Button>
                            <p className="text-muted-foreground text-xs">{t('profile:jpgGifOrPng')}</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-end space-x-2 border-t px-6 py-4">
                      <Button variant="outline" type="button" onClick={() => setActiveTab('overview')}>
                        {t('profile:cancel')}
                      </Button>
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? t('profile:saving') : t('profile:saveChanges')}
                      </Button>
                    </CardFooter>
                  </form>
                </Card>
              </TabsContent>
              {DISPLAY_REFERRAL && <TabsContent value="referral" className="mt-4">
                <Card className="w-full border border-border shadow-sm">
                  <CardContent className="flex items-center justify-center py-12">
                    <p className="text-sm text-muted-foreground">{t('profile:referralProgram')} — redirecting...</p>
                  </CardContent>
                </Card>
              </TabsContent>
              }
            </Tabs>
          </div>
        </div>
      </div>
    </main>
  );
}
